import { useEffect, useState, useCallback } from 'react';
import {
  Activity, Mail, Phone, Target, MousePointerClick, Plus, Check, Trash2,
  Loader2, ListTodo, AlertCircle, X, ChevronRight, Pencil,
} from 'lucide-react';
import { activityService, type ActivityItem, type FollowUp } from '../activityService';
import { PageHeader } from '@/components/common/PageHeader';

const TYPE_ICON: Record<string, any> = { email: Mail, event: MousePointerClick, call: Phone, mql: Target };
const TYPE_COLOR: Record<string, string> = {
  email: 'text-blue-500 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900',
  event: 'text-violet-500 bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-900',
  call: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900',
  mql: 'text-amber-500 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900',
};
const PRI: Record<string, string> = { high: 'bg-red-500', normal: 'bg-amber-400', low: 'bg-gray-400' };

const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
const dayLabel = (iso: string) => {
  const d = new Date(iso); const diff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' });
};
const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
const overdue = (f: FollowUp) => !!f.dueDate && !f.done && startOfDay(new Date(f.dueDate)) < startOfDay(new Date());
const dueToday = (f: FollowUp) => !!f.dueDate && !f.done && startOfDay(new Date(f.dueDate)) === startOfDay(new Date());

const shortLabel = (it: ActivityItem) => {
  if (it.refType === 'EMAIL') return 'Sent';
  if (it.refType === 'MQL') return 'MQL';
  if (it.refType === 'CALL' || it.refType === 'MANUAL_CALL') return 'Call';
  // Prefer the raw engagement event type — it's authoritative. Fall back to a
  // title keyword match only for older cached rows without subType.
  const repeat = (it.occurrence || 1) > 1;   // 2nd+ genuine occurrence = re-engagement
  const EVENT_LABEL: Record<string, string> = {
    OPEN: repeat ? 'Re-opened' : 'Opened', CLICK: 'Clicked', DOWNLOAD: 'Downloaded',
    VIEW: repeat ? 'Re-visited' : 'Viewed', REVISIT: 'Re-visited',
    FORM_VIEW: 'Form', FORM_START: 'Form', FORM_SUBMIT: 'Form',
    VIDEO_PLAY: 'Video', VIDEO_COMPLETE: 'Video', ASSESSMENT_COMPLETE: 'Assessment',
    LIKE: 'Liked', SHARE: 'Shared',
  };
  if (it.subType && EVENT_LABEL[it.subType]) return EVENT_LABEL[it.subType];
  const t = (it.title || '').toLowerCase();
  if (t.includes('open')) return repeat ? 'Re-opened' : 'Opened';
  if (t.includes('landed')) return repeat ? 'Re-opened' : 'Opened';   // legacy label — OPEN event
  if (t.includes('click')) return 'Clicked';
  if (t.includes('download')) return 'Downloaded';
  if (t.includes('view')) return 'Viewed';
  if (t.includes('form') || t.includes('submit')) return 'Form';
  if (t.includes('video')) return 'Video';
  if (t.includes('revisit')) return 'Revisit';
  return (it.title || 'Event').split(' ')[0];
};

// ── Shared editable follow-up row (used in card + sidebar) ─────────────────────
function FollowUpRow({ f, onToggle, onDelete, onEdit, showContact = true }: {
  f: FollowUp;
  onToggle: (f: FollowUp) => void;
  onDelete: (f: FollowUp) => void;
  onEdit: (f: FollowUp, patch: Partial<FollowUp>) => void;
  showContact?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [t, setT] = useState(f.title);
  const [due, setDue] = useState(f.dueDate ? f.dueDate.slice(0, 10) : '');
  const [pri, setPri] = useState<'low' | 'normal' | 'high'>(f.priority);

  const save = () => { onEdit(f, { title: t.trim() || f.title, dueDate: due || null, priority: pri }); setEditing(false); };

  if (editing) {
    return (
      <div className="rounded-md border border-primary/40 p-2 space-y-1.5">
        <input value={t} onChange={e => setT(e.target.value)} className="w-full text-xs px-2 py-1 rounded border border-border bg-background" />
        <div className="flex gap-1.5">
          <input type="date" value={due} onChange={e => setDue(e.target.value)} className="flex-1 text-xs px-2 py-1 rounded border border-border bg-background" />
          <select value={pri} onChange={e => setPri(e.target.value as any)} className="text-xs px-1 py-1 rounded border border-border bg-background">
            <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
          </select>
        </div>
        <div className="flex gap-1.5">
          <button onClick={save} className="text-xs px-2 py-0.5 rounded bg-primary text-primary-foreground">Save</button>
          <button onClick={() => setEditing(false)} className="text-xs px-2 py-0.5 rounded border border-border">Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex items-start gap-2 rounded-md border px-2 py-1.5 ${overdue(f) ? 'border-red-300 bg-red-50 dark:bg-red-950/20' : dueToday(f) ? 'border-amber-300 bg-amber-50 dark:bg-amber-950/20' : 'border-border'}`}>
      <button onClick={() => onToggle(f)} className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 mt-0.5 ${f.done ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'}`}>{f.done && <Check className="h-3 w-3" />}</button>
      <div className="flex-1 min-w-0">
        <p className={`text-xs ${f.done ? 'line-through text-muted-foreground' : ''}`}><span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 ${PRI[f.priority] || PRI.normal}`} />{f.title}</p>
        {showContact && (f.contactName || f.accountName) && <p className="text-[10px] text-muted-foreground">{[f.contactName, f.accountName].filter(Boolean).join(' · ')}</p>}
        {f.dueDate && <p className={`text-[10px] ${overdue(f) ? 'text-red-600' : dueToday(f) ? 'text-amber-600' : 'text-muted-foreground'}`}>{overdue(f) ? 'Overdue · ' : dueToday(f) ? 'Due today · ' : 'Due '}{new Date(f.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>}
      </div>
      <button onClick={() => setEditing(true)} className="text-muted-foreground hover:text-primary shrink-0" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
      <button onClick={() => onDelete(f)} className="text-muted-foreground hover:text-red-500 shrink-0" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// ── One contact's activity for one day: capsule rows + remarks + to-dos ────────
function DayContactCard({ contactName, accountName, items, contactFollowUps, onRemark, onCreateFollowUp, onToggleFollowUp, onEditFollowUp, onDeleteFollowUp }: {
  contactName?: string; accountName?: string; items: ActivityItem[];
  contactFollowUps: FollowUp[];
  onRemark: (item: ActivityItem, note: string) => Promise<void>;
  onCreateFollowUp: (contactId: string, fields: { title: string; dueDate?: string | null; priority?: string }) => Promise<void>;
  onToggleFollowUp: (f: FollowUp) => void;
  onEditFollowUp: (f: FollowUp, patch: Partial<FollowUp>) => void;
  onDeleteFollowUp: (f: FollowUp) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [showTodos, setShowTodos] = useState(false);
  const [fuTitle, setFuTitle] = useState('');
  const [fuDue, setFuDue] = useState('');
  const [fuPri, setFuPri] = useState<'low' | 'normal' | 'high'>('normal');

  const contactId = items.find(i => i.contactId)?.contactId || null;
  const selected = items.find(i => i.id === selectedId) || null;
  const openCount = contactFollowUps.filter(f => !f.done).length;

  // Rows: one per magnet, one per call, one per MQL.
  const rowMap = new Map<string, { key: string; label: string; items: ActivityItem[] }>();
  for (const it of items) {
    let key: string, label: string;
    if (it.magnetId) { key = `magnet:${it.magnetId}`; label = it.magnetTitle || 'Magnet'; }
    else if (it.refType === 'CALL' || it.refType === 'MANUAL_CALL') { key = `call:${it.id}`; label = it.title; }
    else if (it.refType === 'MQL') { key = `mql:${it.id}`; label = 'MQL submission'; }
    else { key = `misc:${it.id}`; label = shortLabel(it); }
    if (!rowMap.has(key)) rowMap.set(key, { key, label, items: [] });
    rowMap.get(key)!.items.push(it);
  }
  const rows = [...rowMap.values()];
  rows.forEach(r => r.items.sort((a, b) => new Date(a.when).getTime() - new Date(b.when).getTime()));
  rows.sort((a, b) => new Date(a.items[0].when).getTime() - new Date(b.items[0].when).getTime());

  const saveRemark = async () => {
    if (!selected || !note.trim()) return;
    setSaving(true);
    try { await onRemark(selected, note.trim()); setNote(''); }
    finally { setSaving(false); }
  };
  const addTodo = async () => {
    if (!contactId || !fuTitle.trim()) return;
    await onCreateFollowUp(contactId, { title: fuTitle.trim(), dueDate: fuDue || null, priority: fuPri });
    setFuTitle(''); setFuDue(''); setFuPri('normal'); setShowTodos(true);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{contactName || accountName || 'Unknown'}</p>
          {contactName && accountName && <p className="text-xs text-muted-foreground truncate">{accountName}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {contactId && (
            <button onClick={() => setShowTodos(s => !s)}
              className={`text-[11px] flex items-center gap-1 px-2 py-0.5 rounded-full border ${openCount ? 'border-primary/40 text-primary' : 'border-border text-muted-foreground'}`}>
              <ListTodo className="h-3 w-3" /> {openCount || 'To-dos'}
            </button>
          )}
          <span className="text-[11px] text-muted-foreground">{items.length} interaction{items.length === 1 ? '' : 's'}</span>
        </div>
      </div>

      {/* One horizontal scrolling line per magnet / call / MQL */}
      <div className="space-y-2">
        {rows.map(row => (
          <div key={row.key} className="flex items-center gap-2">
            <div className="w-28 sm:w-36 shrink-0 text-xs font-medium text-muted-foreground truncate" title={row.label}>{row.label}</div>
            <div className="flex-1 min-w-0 overflow-x-auto">
              <div className="flex items-center w-max py-0.5">
                {row.items.map((it, idx) => {
                  const Icon = TYPE_ICON[it.type] || Activity;
                  const active = it.id === selectedId;
                  return (
                    <div key={it.id} className="flex items-center">
                      <button
                        onClick={() => { setSelectedId(active ? null : it.id); setNote(''); }}
                        className={`flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full border text-xs whitespace-nowrap transition-colors ${TYPE_COLOR[it.type] || 'bg-muted border-border'} ${active ? 'ring-2 ring-primary/40' : ''}`}
                        title={it.title}
                      >
                        <span className="h-5 w-5 rounded-full bg-white/70 dark:bg-black/20 flex items-center justify-center shrink-0"><Icon className="h-3 w-3" /></span>
                        <span className="font-medium">{shortLabel(it)}</span>
                        <span className="opacity-60">{timeLabel(it.when)}</span>
                        {it.remarks.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
                      </button>
                      {idx < row.items.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 mx-0.5 shrink-0" />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Selected capsule detail: remarks + add remark + follow-up */}
      {selected && (
        <div className="mt-3 border-t border-border pt-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium">{selected.title}</p>
              {selected.subtitle && <p className="text-xs text-muted-foreground">{selected.subtitle}</p>}
              <p className="text-[11px] text-muted-foreground">{timeLabel(selected.when)}</p>
            </div>
            <button onClick={() => setSelectedId(null)} className="text-muted-foreground shrink-0"><X className="h-3.5 w-3.5" /></button>
          </div>
          {selected.remarks.length > 0 && (
            <div className="space-y-1">
              {selected.remarks.map(r => (
                <div key={r.id} className="text-xs bg-muted/40 rounded-md px-2 py-1">
                  <span className="text-foreground/90">{r.note}</span>
                  <span className="text-muted-foreground"> — {r.by || 'you'}, {timeLabel(r.at)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <input value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveRemark(); }}
              placeholder="Add a remark…" className="flex-1 text-xs px-2 py-1 rounded border border-border bg-background" />
            <button onClick={saveRemark} disabled={saving || !note.trim()} className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground disabled:opacity-50">{saving ? '…' : 'Save'}</button>
            {selected.contactId && (
              <button onClick={() => onCreateFollowUp(selected.contactId!, { title: `Follow up: ${selected.title}` })}
                className="text-xs px-2 py-1 rounded-lg border border-border hover:bg-muted flex items-center gap-1"><ListTodo className="h-3.5 w-3.5" /> Follow up</button>
            )}
          </div>
        </div>
      )}

      {/* Per-contact to-do list */}
      {contactId && showTodos && (
        <div className="mt-3 border-t border-border pt-3 space-y-2">
          <p className="text-xs font-semibold flex items-center gap-1"><ListTodo className="h-3.5 w-3.5 text-primary" /> Follow-ups for {contactName || 'this contact'}</p>
          <div className="flex gap-1.5">
            <input value={fuTitle} onChange={e => setFuTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addTodo(); }}
              placeholder="Add a to-do for this contact…" className="flex-1 text-xs px-2 py-1 rounded border border-border bg-background" />
            <input type="date" value={fuDue} onChange={e => setFuDue(e.target.value)} className="text-xs px-2 py-1 rounded border border-border bg-background" />
            <select value={fuPri} onChange={e => setFuPri(e.target.value as any)} className="text-xs px-1 py-1 rounded border border-border bg-background">
              <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
            </select>
            <button onClick={addTodo} disabled={!fuTitle.trim()} className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground disabled:opacity-50 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /></button>
          </div>
          {contactFollowUps.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">No follow-ups for this contact yet.</p>
          ) : (
            <div className="space-y-1.5">
              {contactFollowUps.map(f => (
                <FollowUpRow key={f.id} f={f} showContact={false} onToggle={onToggleFollowUp} onDelete={onDeleteFollowUp} onEdit={onEditFollowUp} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Follow-ups sidebar (all contacts) ──────────────────────────────────────────
function FollowUpPanel({ followUps, loading, onCreate, onToggle, onEdit, onDelete }: {
  followUps: FollowUp[]; loading: boolean;
  onCreate: (fields: { title: string; dueDate?: string | null; priority?: string }) => Promise<void>;
  onToggle: (f: FollowUp) => void; onEdit: (f: FollowUp, patch: Partial<FollowUp>) => void; onDelete: (f: FollowUp) => void;
}) {
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [showDone, setShowDone] = useState(false);
  const visible = followUps.filter(f => showDone || !f.done);

  const add = async () => {
    if (!title.trim()) return;
    await onCreate({ title: title.trim(), dueDate: due || null, priority });
    setTitle(''); setDue(''); setPriority('normal');
  };

  return (
    <div className="rounded-xl border border-border p-4 space-y-3 h-fit sticky top-4">
      <div className="flex items-center gap-2"><ListTodo className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold">All follow-ups</h2></div>
      <div className="space-y-1.5">
        <input value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }} placeholder="New follow-up…" className="w-full text-sm px-2.5 py-1.5 rounded-md border border-border bg-background" />
        <div className="flex gap-1.5">
          <input type="date" value={due} onChange={e => setDue(e.target.value)} className="flex-1 text-xs px-2 py-1.5 rounded-md border border-border bg-background" />
          <select value={priority} onChange={e => setPriority(e.target.value as any)} className="text-xs px-2 py-1.5 rounded-md border border-border bg-background"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></select>
          <button onClick={add} disabled={!title.trim()} className="px-2.5 rounded-md bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
        </div>
      </div>
      <button onClick={() => setShowDone(s => !s)} className="text-[11px] text-muted-foreground hover:text-foreground">{showDone ? 'Hide completed' : 'Show completed'}</button>
      {loading ? (<div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>)
        : visible.length === 0 ? (<p className="text-xs text-muted-foreground py-2">Nothing pending. Add a follow-up above.</p>)
          : (<div className="space-y-1.5">{visible.map(f => <FollowUpRow key={f.id} f={f} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} />)}</div>)}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function ActivityPage() {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [error, setError] = useState<string | null>(null);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [fuLoading, setFuLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    activityService.getFeed(days).then(r => setItems(r.items)).catch(e => setError(e?.response?.data?.error || 'Failed to load activity')).finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const loadFollowUps = useCallback(() => {
    setFuLoading(true);
    activityService.getFollowUps(undefined).then(r => setFollowUps(r.items)).catch(() => setFollowUps([])).finally(() => setFuLoading(false));
  }, []);
  useEffect(() => { loadFollowUps(); }, [loadFollowUps]);

  const addRemark = async (item: ActivityItem, note: string) => {
    const r = await activityService.addRemark({ refType: item.refType, refId: item.refId, contactId: item.contactId, note });
    setItems(prev => prev.map(it => it.id === item.id ? { ...it, remarks: [...it.remarks, r] } : it));
  };
  const createFollowUpForContact = async (contactId: string, fields: { title: string; dueDate?: string | null; priority?: string }) => {
    await activityService.createFollowUp({ ...fields, contactId: contactId || null }); loadFollowUps();
  };
  const createFollowUpGlobal = async (fields: { title: string; dueDate?: string | null; priority?: string }) => {
    await activityService.createFollowUp(fields); loadFollowUps();
  };
  const toggleFollowUp = async (f: FollowUp) => { await activityService.updateFollowUp(f.id, { done: !f.done }); loadFollowUps(); };
  const editFollowUp = async (f: FollowUp, patch: Partial<FollowUp>) => { await activityService.updateFollowUp(f.id, patch); loadFollowUps(); };
  const deleteFollowUp = async (f: FollowUp) => { await activityService.deleteFollowUp(f.id); loadFollowUps(); };

  // Group: day (desc) → contact → items (asc).
  type ContactGroup = { key: string; contactId: string | null; contactName?: string; accountName?: string; items: ActivityItem[] };
  type DayGroup = { label: string; count: number; contacts: ContactGroup[] };
  const dayGroups: DayGroup[] = [];
  for (const it of items) {
    const dkey = dayLabel(it.when);
    let dg = dayGroups.find(d => d.label === dkey);
    if (!dg) { dg = { label: dkey, count: 0, contacts: [] }; dayGroups.push(dg); }
    dg.count++;
    const ckey = it.contactId || `acct:${it.accountName || 'none'}`;
    let cg = dg.contacts.find(c => c.key === ckey);
    if (!cg) { cg = { key: ckey, contactId: it.contactId || null, contactName: it.contactName, accountName: it.accountName, items: [] }; dg.contacts.push(cg); }
    cg.items.push(it);
  }
  for (const dg of dayGroups) for (const cg of dg.contacts) cg.items.sort((a, b) => new Date(a.when).getTime() - new Date(b.when).getTime());

  const fuByContact = new Map<string, FollowUp[]>();
  for (const f of followUps) { if (f.contactId) { if (!fuByContact.has(f.contactId)) fuByContact.set(f.contactId, []); fuByContact.get(f.contactId)!.push(f); } }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <PageHeader title="Customer Activity" description="One card per contact each day — interactions as capsule steppers, with per-contact remarks and to-dos so nothing slips." icon={Activity} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
        <div className="lg:col-span-2 space-y-5">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">Show last</span>
            {[7, 14, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)} className={`text-xs px-2.5 py-1 rounded-lg border ${days === d ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/40'}`}>{d}d</button>
            ))}
          </div>
          {error && (<div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 p-3 text-sm text-red-700 dark:text-red-400"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span></div>)}
          {loading ? (<div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>)
            : dayGroups.length === 0 ? (<div className="text-center py-12 text-sm text-muted-foreground">No interactions in this window.</div>)
              : (dayGroups.map(dg => (
                <div key={dg.label} className="space-y-2">
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-xs font-semibold">{dg.label}</span>
                    <span className="text-[11px] text-muted-foreground">· {dg.count} interaction{dg.count === 1 ? '' : 's'} · {dg.contacts.length} contact{dg.contacts.length === 1 ? '' : 's'}</span>
                  </div>
                  {dg.contacts.map(cg => (
                    <DayContactCard key={cg.key} contactName={cg.contactName} accountName={cg.accountName} items={cg.items}
                      contactFollowUps={cg.contactId ? (fuByContact.get(cg.contactId) || []) : []}
                      onRemark={addRemark} onCreateFollowUp={createFollowUpForContact}
                      onToggleFollowUp={toggleFollowUp} onEditFollowUp={editFollowUp} onDeleteFollowUp={deleteFollowUp} />
                  ))}
                </div>
              )))}
        </div>
        <div><FollowUpPanel followUps={followUps} loading={fuLoading} onCreate={createFollowUpGlobal} onToggle={toggleFollowUp} onEdit={editFollowUp} onDelete={deleteFollowUp} /></div>
      </div>
    </div>
  );
}
